import api from '@/api';
import { useCookies } from '@vueuse/integrations/useCookies';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';

const cookies = useCookies();

/**
 * Resolves the effective broker id for the current session, identifies it with
 * Flagsmith so identity-based segments are evaluated, and records the broker's
 * backend-resolved ERHL access.
 *
 * Mirrors the broker-resolution logic used by the application layouts: when a
 * broker assistant has selected a broker (via the `broker-id` cookie), the
 * selected broker is used; otherwise the authenticated broker is used.
 *
 * ERHL access always belongs to the broker being acted for, so for an assistant
 * session it comes from the selected broker's response — the assistant's own
 * current-user payload carries none, and falling back to it would open ERHL for
 * every assistant of an enabled origin type.
 *
 * This is intended to be called from places that need identity-gated flags or the
 * ERHL gate before the application layout has mounted (e.g. a route guard handling
 * a deep link). It swallows errors and returns the resolved id (or `null`) so
 * callers can apply their own safe-default behaviour.
 *
 * @returns The resolved broker id that was identified, or `null` if resolution
 *   failed (e.g. unauthenticated). When `null`, the ERHL gate and identity-gated
 *   flags fall back to their closed default.
 */
export const resolveBrokerIdentity = async (): Promise<string | null> => {
  const { identify, setErhlAccess } = useFeatureFlags();

  try {
    const { data } = await api.backend.brokers.user.currentUser();
    const brokerUser = data.broker;

    let brokerId: string | undefined = brokerUser.id;
    const selectedBrokerId = cookies.get('broker-id');

    // Resolved alongside the id so both describe the same broker. Assistants start
    // closed and only open if the selected broker validates below.
    let erhlAccess: boolean | undefined = 'hasErhlAccess' in brokerUser ? brokerUser.hasErhlAccess : undefined;

    if (
      'brokerAssistantReferenceNumber' in brokerUser &&
      brokerUser.brokerAssistantReferenceNumber &&
      selectedBrokerId
    ) {
      // Broker-assistant session: confirm the selected broker via the BE
      // (origin-type scoped — the same call the application layouts make) before
      // trusting the `broker-id` cookie. This keeps the Flagsmith identity from
      // diverging from a tampered/stale cookie within a session. On failure we
      // fall back to the assistant's own id rather than trusting the cookie.
      erhlAccess = undefined;
      try {
        const { data: brokerData } = await api.backend.brokerAssistants.broker.retrieveBroker({
          id: selectedBrokerId,
        });
        if (brokerData?.broker) {
          brokerId = selectedBrokerId;
          erhlAccess = brokerData.broker.hasErhlAccess;
        }
      } catch (error) {
        console.error('Failed to validate selected broker for feature flags:', error);
      }
    }

    // After identify(), which clears the previous broker's resolved access when the
    // identity changes — setting it first would be undone.
    await identify(brokerId);
    setErhlAccess(erhlAccess);
    return brokerId ?? null;
  } catch (error) {
    console.error('Failed to resolve broker identity for feature flags:', error);
    setErhlAccess(null);
    return null;
  }
};
